import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  LogOut,
  Users,
  Plus,
  Edit,
  Trash2,
  ShieldCheck,
  Building2,
  FlaskConical,
  Receipt,
  ArrowLeft,
  Eye,
  EyeOff,
  UserCheck,
  UserX,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Role Badge Component
const RoleBadge = ({ rol, rolLabel }) => {
  const styles = {
    super_admin: 'bg-purple-100 text-purple-700 border-purple-200',
    contador: 'bg-blue-100 text-blue-700 border-blue-200',
    operador: 'bg-green-100 text-green-700 border-green-200',
    consulta: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${styles[rol] || styles.consulta}`}>
      <ShieldCheck className="w-3.5 h-3.5" />
      {rolLabel || rol}
    </span>
  );
};

// Status Badge Component
const StatusBadge = ({ activo }) => (
  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
    activo ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'
  }`}>
    {activo ? <UserCheck className="w-3.5 h-3.5" /> : <UserX className="w-3.5 h-3.5" />}
    {activo ? 'Activo' : 'Inactivo'}
  </span>
);

export default function AdminUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showCustomPermissions, setShowCustomPermissions] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'consulta',
    activo: true,
    permisos_personalizados: null,
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchCurrentUser(),
        fetchUsuarios(),
        fetchRoles(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/admin/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchUsuarios = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsuarios(response.data || []);
    } catch (error) {
      console.error('Error fetching usuarios:', error);
      if (error.response?.status === 403) {
        toast.error('No tiene permiso para ver usuarios');
      }
    }
  };

  const fetchRoles = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/admin/users/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoles(response.data.roles || []);
      setModulos(response.data.modulos || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const openCreateModal = () => {
    setModalMode('create');
    setEditingUser(null);
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rol: 'consulta',
      activo: true,
      permisos_personalizados: null,
    });
    setShowCustomPermissions(false);
    setShowPassword(false);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setModalMode('edit');
    setEditingUser(user);
    setFormData({
      nombre: user.nombre,
      email: user.email,
      password: '',
      rol: user.rol,
      activo: user.activo,
      permisos_personalizados: user.permisos || null,
    });
    setShowCustomPermissions(!!user.permisos_personalizados);
    setShowPassword(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      const payload = {
        nombre: formData.nombre,
        email: formData.email,
        rol: formData.rol,
        activo: formData.activo,
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      if (showCustomPermissions && formData.permisos_personalizados) {
        payload.permisos_personalizados = formData.permisos_personalizados;
      }

      if (modalMode === 'create') {
        if (!formData.password) {
          toast.error('La contraseña es requerida');
          return;
        }
        await axios.post(`${API}/admin/users`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Usuario creado exitosamente');
      } else {
        await axios.put(`${API}/admin/users/${editingUser.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Usuario actualizado exitosamente');
      }

      setShowModal(false);
      fetchUsuarios();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Usuario eliminado');
      fetchUsuarios();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleToggleActive = async (user) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/admin/users/${user.id}`, {
        activo: !user.activo
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(user.activo ? 'Usuario desactivado' : 'Usuario activado');
      fetchUsuarios();
    } catch (error) {
      console.error('Error toggling user:', error);
      toast.error('Error al cambiar estado del usuario');
    }
  };

  const updateCustomPermission = (modulo, accion, checked) => {
    setFormData(prev => {
      const currentPermisos = prev.permisos_personalizados || {
        dashboard: [],
        clinicas: [],
        catalogo: [],
        solicitudes: [],
        reportes: [],
        usuarios: [],
      };

      const moduloPermisos = currentPermisos[modulo] || [];
      let newModuloPermisos;

      if (checked) {
        newModuloPermisos = [...moduloPermisos, accion];
      } else {
        newModuloPermisos = moduloPermisos.filter(a => a !== accion);
      }

      return {
        ...prev,
        permisos_personalizados: {
          ...currentPermisos,
          [modulo]: newModuloPermisos,
        },
      };
    });
  };

  const getDefaultPermissionsForRole = (rol) => {
    const roleConfig = roles.find(r => r.value === rol);
    return roleConfig?.permisos || {};
  };

  const hasPermission = (permisos) => {
    if (!permisos) return false;
    return permisos.usuarios?.includes('ver') || currentUser?.rol === 'super_admin' || currentUser?.id === 'master';
  };

  const canCreate = () => {
    if (!currentUser) return false;
    return currentUser.permisos?.usuarios?.includes('crear') || currentUser.id === 'master';
  };

  const canEdit = () => {
    if (!currentUser) return false;
    return currentUser.permisos?.usuarios?.includes('editar') || currentUser.id === 'master';
  };

  const canDelete = () => {
    if (!currentUser) return false;
    return currentUser.permisos?.usuarios?.includes('eliminar') || currentUser.id === 'master';
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-9" />
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="h-10 border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/admin/clinicas')}
                variant="outline"
                className="h-10 border-slate-300"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Clínicas
              </Button>
              <Button
                onClick={() => navigate('/admin/catalogo')}
                variant="outline"
                className="h-10 border-slate-300"
              >
                <FlaskConical className="w-4 h-4 mr-2" />
                Catálogo
              </Button>
              <Button
                onClick={() => navigate('/admin/reportes')}
                variant="outline"
                className="h-10 border-slate-300"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Reportes
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="h-10 border-slate-300"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Page Header */}
      <div className="bg-[#505BB3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Gestión de Usuarios</h1>
                <p className="text-white/80 text-sm">Administre los usuarios del panel de administración</p>
              </div>
            </div>
            {canCreate() && (
              <Button
                onClick={openCreateModal}
                className="bg-white text-[#505BB3] hover:bg-white/90"
                data-testid="create-user-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Usuario
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Current User Info */}
        {currentUser && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-blue-800">
                  Sesión activa: <strong>{currentUser.nombre}</strong> ({currentUser.email})
                </p>
                <p className="text-xs text-blue-600">
                  Rol: {currentUser.rol_label}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#505BB3]" />
              Usuarios Administrativos ({usuarios.length})
            </h2>
          </div>

          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-12 text-center text-slate-500">
                <div className="animate-spin w-8 h-8 border-2 border-[#505BB3] border-t-transparent rounded-full mx-auto mb-4"></div>
                Cargando usuarios...
              </div>
            ) : usuarios.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-1">Sin usuarios</h3>
                <p className="text-slate-500 mb-4">Aún no hay usuarios administrativos creados</p>
                {canCreate() && (
                  <Button onClick={openCreateModal} className="bg-[#505BB3] hover:bg-[#404A9C] text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Primer Usuario
                  </Button>
                )}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Rol</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Último Acceso</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {usuarios.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-slate-900">{user.nombre}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-600">{user.email}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <RoleBadge rol={user.rol} rolLabel={user.rol_label} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge activo={user.activo} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-500">
                          {user.ultimo_acceso 
                            ? new Date(user.ultimo_acceso).toLocaleString('es-MX')
                            : 'Nunca'
                          }
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canEdit() && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditModal(user)}
                                className="h-8"
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleActive(user)}
                                className={`h-8 ${user.activo ? 'text-amber-600 border-amber-200 hover:bg-amber-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
                                data-testid={`toggle-user-${user.id}`}
                              >
                                {user.activo ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                              </Button>
                            </>
                          )}
                          {canDelete() && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(user.id)}
                              className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {modalMode === 'create' ? <Plus className="w-5 h-5" /> : <Edit className="w-5 h-5" />}
              {modalMode === 'create' ? 'Nuevo Usuario' : 'Editar Usuario'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre Completo</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre del usuario"
                  required
                  data-testid="user-nombre"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  required
                  data-testid="user-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">
                  Contraseña {modalMode === 'edit' && <span className="text-slate-400">(dejar vacío para no cambiar)</span>}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={modalMode === 'create' ? 'Contraseña' : '••••••••'}
                    required={modalMode === 'create'}
                    data-testid="user-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rol">Rol</Label>
                <Select
                  value={formData.rol}
                  onValueChange={(value) => setFormData({ ...formData, rol: value })}
                >
                  <SelectTrigger data-testid="user-rol">
                    <SelectValue placeholder="Seleccionar rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Role Permissions Preview */}
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <h4 className="font-medium text-sm text-slate-700 mb-2">
                Permisos del rol "{roles.find(r => r.value === formData.rol)?.label || formData.rol}"
              </h4>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {modulos.map((modulo) => {
                  const permsForRole = getDefaultPermissionsForRole(formData.rol);
                  const moduloPerms = permsForRole[modulo.id] || [];
                  return (
                    <div key={modulo.id} className="bg-white rounded p-2 border border-slate-100">
                      <span className="font-medium text-slate-600">{modulo.label}:</span>
                      <span className="ml-1 text-slate-500">
                        {moduloPerms.length > 0 ? moduloPerms.join(', ') : 'Sin acceso'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Custom Permissions Toggle */}
            <div className="border border-slate-200 rounded-lg">
              <button
                type="button"
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 rounded-t-lg"
                onClick={() => setShowCustomPermissions(!showCustomPermissions)}
              >
                <span className="font-medium text-sm text-slate-700">
                  Personalizar permisos (opcional)
                </span>
                {showCustomPermissions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>

              {showCustomPermissions && (
                <div className="p-4 border-t border-slate-200 space-y-4">
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    Al personalizar permisos, estos reemplazan los permisos del rol seleccionado.
                  </p>
                  {modulos.map((modulo) => (
                    <div key={modulo.id} className="space-y-2">
                      <Label className="text-sm font-medium">{modulo.label}</Label>
                      <div className="flex flex-wrap gap-3">
                        {modulo.acciones.map((accion) => (
                          <label key={accion} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={formData.permisos_personalizados?.[modulo.id]?.includes(accion) || false}
                              onCheckedChange={(checked) => updateCustomPermission(modulo.id, accion, checked)}
                            />
                            <span className="capitalize">{accion.replace('_', ' ')}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Active Status */}
            {modalMode === 'edit' && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="activo"
                  checked={formData.activo}
                  onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
                />
                <Label htmlFor="activo">Usuario activo</Label>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#505BB3] hover:bg-[#404A9C]" data-testid="save-user-btn">
                {modalMode === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
